import {
    useState,
    useEffect,
    useCallback,
    useRef,
    type FC,
    type KeyboardEvent,
    type FocusEvent,
    type MouseEvent,
    type FormEventHandler,
} from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import classNames from 'classnames';
import { useIntl, defineMessages, FormattedMessage } from 'dibs-react-intl';
import { SearchBar as SearchBarElement } from 'dibs-elements/exports/SearchBar';
import { SIZES } from 'dibs-elements/exports/constants';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import { useDeviceType } from '../hooks/useDeviceType';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { defaultAvatarGradient } from 'dibs-crypto-utils/exports/formattingUtils';
import { NftItemTileMedia } from 'dibs-nft-item-tile/exports/NftItemTileMedia';

import { NftSearchBarOverlay } from './NftSearchBarOverlay';
import {
    dedupeLocalStorage,
    getCursorTerms,
    handleRecentStorage,
    cleanUpRecentSearches,
} from './shared/nftSearchBarHelpers';
import {
    KEY_DOWN_VALUE,
    KEY_UP_VALUE,
    SEARCH_PAGE_PATH,
    SEARCH_QUERY_PARAM,
    type SuggestionType,
    type CursorType,
} from './shared/nftSearchBarConstants';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback';

import { type NftSearchBarRefetchQuery } from './__generated__/NftSearchBarRefetchQuery.graphql';
import {
    type NftSearchBar_viewer$key,
    type NftSearchBar_viewer$data,
} from './__generated__/NftSearchBar_viewer.graphql';

import styles from './NftSearchBar.scss';

const MIN_NUM_VALUE_CHARACTERS = 2;
const SEARCH_BAR_DATA_TN = 'global-header-search-input';
const ELEMENTS_PREVENTING_SEARCH_BAR_SHRINK = [
    `${SEARCH_BAR_DATA_TN}-search-button`,
    `${SEARCH_BAR_DATA_TN}-clear`,
    // in some OS + browsers combos buttons are not focused on click, then tabbable wrapper div gets the focus
    `${SEARCH_BAR_DATA_TN}-search-button-wrapper`,
    `${SEARCH_BAR_DATA_TN}-clear-wrapper`,
];

const labels = defineMessages({
    searchBarPlaceholder: { id: 'NftSearchBar.placeholder', defaultMessage: 'Search' },
});

const refetchableFragment = graphql`
    fragment NftSearchBar_viewer on Viewer
    @refetchable(queryName: "NftSearchBarRefetchQuery")
    @argumentDefinitions(
        query: { type: "String", defaultValue: "" }
        hasQuery: { type: "Boolean", defaultValue: false }
    ) {
        nftSearchSuggestion(query: $query, includeTypes: ["ITEMS", "CREATORS", "EXHIBITIONS"])
            @include(if: $hasQuery) {
            items {
                pdpUrl
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                    }
                }
                creatorAddress {
                    publicProfile {
                        profileName
                    }
                }
                ...NftItemTileMedia_item
            }
            creators {
                address {
                    address
                    publicProfile {
                        profileName
                        avatarImage {
                            path
                        }
                    }
                    publicProfileUrl
                }
                nftItemCount
            }
            exhibitions {
                pageName
                modules {
                    heroBanner {
                        desktopImage {
                            url
                        }
                        bannerConfig {
                            ... on NftCmsExhibitionHtmlBannerType {
                                eyeBrow
                            }
                        }
                    }
                }
            }
        }
    }
`;

type Props = {
    viewer: NftSearchBar_viewer$key | null | undefined;
};

const buildSuggestions = (
    query: string,
    nftSuggestions: NftSearchBar_viewer$data['nftSearchSuggestion']
): SuggestionType[] => {
    const suggestions: SuggestionType[] = [];
    Object.keys(nftSuggestions || {}).forEach(key => {
        if (key === 'items') {
            suggestions.push({
                type: 'ITEMS',
                terms: nftSuggestions?.items?.map(item => {
                    return {
                        term: item?.metadata?.name || '',
                        subterm: item?.creatorAddress?.publicProfile?.profileName || '',
                        urlLabel: item?.pdpUrl || '',
                        image: (
                            <NftItemTileMedia
                                item={item || null}
                                animate={false}
                                isMobile={false}
                                showIcon={false}
                            />
                        ),
                    };
                }),
            });
        }
        if (key === 'creators') {
            suggestions.push({
                type: 'CREATORS',
                terms: nftSuggestions?.creators?.map(creator => {
                    return {
                        term: creator?.address?.publicProfile?.profileName || '',
                        subterm: (
                            <FormattedMessage
                                id="NftSearchBar.creators.nftItemCount"
                                defaultMessage="{nftItemCount, plural, one {# Creation} other {# Creations}}"
                                values={{
                                    nftItemCount: creator?.nftItemCount,
                                }}
                            />
                        ),
                        urlLabel: creator?.address?.publicProfileUrl || '',
                        image: creator?.address?.publicProfile?.avatarImage?.path ? (
                            <img
                                loading="lazy"
                                src={creator.address.publicProfile.avatarImage.path}
                                alt={`avatar-${creator.address.publicProfile.profileName}`}
                            />
                        ) : null,
                        defaultAvatar: defaultAvatarGradient(creator?.address?.address || ''),
                    };
                }),
            });
        }
        if (key === 'exhibitions') {
            suggestions.push({
                type: 'EXHIBITIONS',
                terms: nftSuggestions?.exhibitions?.map(exhibition => {
                    return {
                        term: exhibition?.modules?.heroBanner?.[0]?.bannerConfig?.eyeBrow || '',
                        urlLabel: `/nft-collections/exhibitions/${exhibition?.pageName || ''}`,
                        image: (
                            <img
                                loading="lazy"
                                src={exhibition?.modules?.heroBanner?.[0]?.desktopImage?.url || ''}
                                alt={`avatar-${exhibition?.modules?.heroBanner?.[0]?.bannerConfig?.eyeBrow}`}
                            />
                        ),
                    };
                }),
            });
        }
    });

    return suggestions.map(suggestion => ({
        ...suggestion,
        terms: suggestion.terms?.map(term => ({
            ...term,
            urlLabel: `${term.urlLabel}?${new URLSearchParams({ search: query }).toString()}`,
        })),
    }));
};

export const NftSearchBar: FC<Props> = ({ viewer: viewerRef }) => {
    const intl = useIntl();
    const isClient = useClientState();
    const { deviceType } = useDeviceType();
    const isTablet = deviceType === 'tablet';
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isSearchBarCleared, setIsSearchBarCleared] = useState(false);
    const [cursorTerms, setCursorTerms] = useState<CursorType[]>([]);
    const [cursorPosition, setCursorPosition] = useState(-1);
    const [cursorHighlightId, setCursorHighlightId] = useState<string | undefined>(undefined);
    const overlayWrapper = useRef<HTMLDivElement>(null);
    const [viewer, refetch] = useRefetchableFragment<
        NftSearchBarRefetchQuery,
        NftSearchBar_viewer$key
    >(refetchableFragment, viewerRef);

    const searchSuggestions = viewer?.nftSearchSuggestion;
    const suggestions = buildSuggestions(value, searchSuggestions);
    const recentSearches = dedupeLocalStorage() || [];
    const dropdownChoices = suggestions.length > 0 ? suggestions : recentSearches;
    const isVisible = isFocused || isHovered;

    const onFocus = useCallback(() => {
        setIsFocused(true);
    }, [setIsFocused]);

    const onBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            const focusedElement = event.relatedTarget?.getAttribute('data-tn') || '';
            if (!ELEMENTS_PREVENTING_SEARCH_BAR_SHRINK.includes(focusedElement)) {
                setIsFocused(false);
            }
        },
        [setIsFocused]
    );

    const onClear = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setIsFocused(false);
        },
        [setIsFocused]
    );

    const onMouseOver = useCallback(() => {
        setIsHovered(true);
    }, [setIsHovered]);

    const onMouseOut = useCallback(() => {
        setIsHovered(false);
    }, [setIsHovered]);

    const setSearchTermFromParams = useCallback(() => {
        const { href, search } = location;
        if (href.indexOf(SEARCH_PAGE_PATH) !== -1) {
            const searchParams = new URLSearchParams(search);
            const searchTerm = searchParams.get(SEARCH_QUERY_PARAM);

            if (searchTerm) {
                setValue(searchTerm);
            }
        }
    }, [setValue]);

    const handleRowClick = useCallback(
        (val: string, href: string) => {
            setValue(val);
            setIsFocused(false);
            setIsHovered(false);
            handleRecentStorage(val, href);
            location.assign(handleLocaleUrl(href, GLOBAL_CLIENT_ONLY_LOCALE));
        },
        [setValue, setIsFocused, setIsHovered]
    );

    const handleCursorChange = useCallback(
        (move: string) => {
            const newCursorTerms = cursorTerms.length
                ? cursorTerms
                : getCursorTerms(dropdownChoices, value);
            let newCursorPosition = cursorPosition;

            if (dropdownChoices.length === 0) {
                // if we have no section values exit out
                return;
            }

            switch (move) {
                case KEY_UP_VALUE:
                    newCursorPosition =
                        cursorPosition || cursorPosition === 0
                            ? (cursorPosition - 1 + newCursorTerms.length) % newCursorTerms.length
                            : newCursorTerms.length - 2;
                    break;
                case KEY_DOWN_VALUE:
                    newCursorPosition =
                        cursorPosition || cursorPosition === 0
                            ? (cursorPosition + 1) % newCursorTerms.length
                            : 0;
                    break;
                default:
                    break;
            }

            const newValue = newCursorTerms[newCursorPosition].term;
            const newHighlightId = newCursorTerms[newCursorPosition].highlightId;
            setValue(newValue);
            setCursorHighlightId(newHighlightId);
            setCursorTerms(newCursorTerms);
            setCursorPosition(newCursorPosition);
        },
        [
            dropdownChoices,
            value,
            cursorPosition,
            cursorTerms,
            setCursorPosition,
            setCursorTerms,
            setCursorHighlightId,
        ]
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.code === 'Escape') {
            setIsFocused(false);
        }

        if (event.code === 'ArrowDown') {
            event.preventDefault();
            handleCursorChange(KEY_DOWN_VALUE);
        }

        if (event.code === 'ArrowUp') {
            event.preventDefault();
            handleCursorChange(KEY_UP_VALUE);
        }
    };

    const fetchSuggestions = useCallback(() => {
        refetch(
            { query: value.toLowerCase(), hasQuery: value.length > 1 },
            { fetchPolicy: 'network-only' }
        );
        setIsSearchBarCleared(false);
    }, [value, refetch]);

    const [debouncedFetchSuggestions] = useDebouncedCallback(fetchSuggestions, 250);

    const handleChange = useCallback(
        (newValue: string) => {
            if (!newValue) {
                setIsSearchBarCleared(true);
            }
            setValue(newValue);
            setCursorTerms([]);
            setCursorHighlightId(undefined);
            setCursorPosition(-1);
        },
        [setValue, setCursorTerms, setCursorPosition]
    );

    const handleSubmit: FormEventHandler = useCallback(
        event => {
            event.preventDefault();
            let urlLabel;

            if (cursorPosition > -1 && cursorTerms[cursorPosition].link) {
                handleRowClick(
                    cursorTerms[cursorPosition].term,
                    cursorTerms[cursorPosition]?.link || ''
                );
            } else if (value) {
                urlLabel = `/nft-collections/search/?q=${encodeURIComponent(value.toLowerCase())}`;
                handleRecentStorage(value, urlLabel);
                location.assign(handleLocaleUrl(urlLabel, GLOBAL_CLIENT_ONLY_LOCALE));
            }
        },
        [value, cursorTerms, cursorPosition, handleRowClick]
    );

    useEffect(() => {
        cleanUpRecentSearches();
    }, []);

    useEffect(() => {
        if (value && !cursorHighlightId) {
            debouncedFetchSuggestions();
        }
    }, [cursorHighlightId, value, debouncedFetchSuggestions]);

    useEffect(() => {
        if (overlayWrapper.current) {
            overlayWrapper.current.addEventListener('mouseover', onMouseOver);
            overlayWrapper.current.addEventListener('mouseout', onMouseOut);
            setSearchTermFromParams();
        }
    }, [overlayWrapper, setSearchTermFromParams, onMouseOut, onMouseOver]);

    const showRecentlyViewed =
        suggestions.length === 0 || value.length < MIN_NUM_VALUE_CHARACTERS || isSearchBarCleared;
    const searchBarClassName = classNames(styles.searchBar, {
        [styles.isActive]: isVisible && !isTablet,
        [styles.isTablet]: isTablet,
    });

    return (
        <div className={styles.wrapper}>
            <form
                className={styles.form}
                action="/nft-collections/search/"
                role="search"
                onSubmit={handleSubmit}
            >
                <div className={searchBarClassName}>
                    <SearchBarElement
                        dataTn={SEARCH_BAR_DATA_TN}
                        isClearButtonHidden={!value}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        onChange={handleChange}
                        onClear={onClear}
                        onKeyDown={handleKeyDown}
                        placeholder={intl.formatMessage(labels.searchBarPlaceholder)}
                        size="medium"
                        value={value}
                        leftMargin={SIZES.small}
                        isHeaderSearch
                    />
                </div>
            </form>
            <div className={styles.overlayWrapper} ref={overlayWrapper}>
                {isClient ? (
                    <NftSearchBarOverlay
                        overlayClassName={styles.overlay}
                        canShowRecentlyViewed={isVisible && showRecentlyViewed}
                        handleRowClick={handleRowClick}
                        visible={isVisible}
                        suggestions={showRecentlyViewed ? recentSearches : suggestions}
                        cursorHighlightId={cursorHighlightId || ''}
                        focusOnOverlayScroll={false}
                    />
                ) : null}
            </div>
        </div>
    );
};
